import React, { useState, useEffect } from "react";
import Editor from "@monaco-editor/react";
import { X, Code2, Loader2, Check, Layers } from "lucide-react";

const SaveSnippetDialog = ({
  isOpen,
  onClose,
  onSave,
  initialName = "",
  snippetData = null,
  theme = "dark", // "dark" | "light"
}) => {
  const [name, setName] = useState(initialName || "");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const isLight = theme === "light";

  useEffect(() => {
    if (isOpen) {
      setName(initialName || "Unnamed Snippet");
      setDescription("");
      setError("");
      setIsSaving(false);
    }
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError("Please enter a name for this snippet.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
      });
      onClose();
    } catch (err) {
      console.error("Failed to save snippet:", err);
      setError(err.message || "Failed to save snippet. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const isBlockSnippet =
    snippetData?.environment === "scratch" || snippetData?.environment === "junior";
  const blockCount = snippetData?.snippetJson?.blockCount;
  const targetName = snippetData?.snippetJson?.targetName;
  const langName = snippetData?.language
    ? snippetData.language.charAt(0).toUpperCase() + snippetData.language.slice(1)
    : "Code";

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden font-sans border-0 ${
          isLight
            ? "bg-white ring-1 ring-neutral-200 text-neutral-800"
            : "bg-[#13151b] ring-1 ring-[#27272a] text-white shadow-[0_25px_60px_rgba(0,0,0,0.9)]"
        }`}
      >
        {/* Header */}
        <div
          className={`px-5 py-4 flex items-center justify-between border-0 ${
            isLight
              ? "bg-neutral-50 border-b border-neutral-200"
              : "bg-[#0c0d12] border-b border-[#222226]"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-600/15 ring-1 ring-purple-500/25 flex items-center justify-center text-purple-400">
              <Code2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`font-bold text-base leading-tight ${isLight ? "text-neutral-900" : "text-white"}`}>
                Save Snippet to Backpack
              </h3>
              <p className={`text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                Reuse this snippet in any of your projects
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
              isLight
                ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-500"
                : "bg-white/5 hover:bg-white/10 text-neutral-300"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Metadata Snippet Type Pill */}
          <div
            className={`p-3 rounded-xl flex items-center justify-between border-0 ${
              isLight
                ? "bg-purple-50/80 ring-1 ring-purple-200/80 text-purple-900"
                : "bg-purple-950/25 ring-1 ring-purple-500/20 text-purple-200"
            }`}
          >
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-400" />
              <span className="text-xs font-bold">
                {isBlockSnippet ? "Scratch Block Stack" : `${langName} Snippet`}
              </span>
            </div>

            {isBlockSnippet && blockCount && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-600/20 text-purple-700 dark:text-purple-300">
                {blockCount} block{blockCount > 1 ? "s" : ""} {targetName ? `(${targetName})` : ""}
              </span>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 ring-1 ring-red-200 dark:ring-red-800/40 text-red-600 dark:text-red-300 text-xs font-semibold">
              {error}
            </div>
          )}

          {/* Snippet Name Field */}
          <div className="space-y-1.5 text-left">
            <label className={`text-xs font-bold flex items-center justify-between ${isLight ? "text-neutral-700" : "text-neutral-200"}`}>
              <span>Snippet Name *</span>
              <span className="text-[10px] text-neutral-400 font-normal">Max 50 chars</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Movement Loop Script"
              maxLength={50}
              autoFocus
              className={`w-full px-3.5 py-2.5 rounded-xl text-sm font-medium outline-none transition border-0 ${
                isLight
                  ? "bg-neutral-50 ring-1 ring-neutral-200 text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:bg-white"
                  : "bg-[#0a0b0f] ring-1 ring-[#27272a] focus:ring-2 focus:ring-purple-500/50 text-white placeholder:text-neutral-500"
              }`}
            />
          </div>

          {/* Snippet Description Field */}
          <div className="space-y-1.5 text-left">
            <label className={`text-xs font-bold ${isLight ? "text-neutral-700" : "text-neutral-200"}`}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Moves the sprite smoothly using repeat loops"
              rows={2}
              maxLength={200}
              className={`w-full px-3.5 py-2 rounded-xl text-sm font-medium outline-none transition resize-none border-0 ${
                isLight
                  ? "bg-neutral-50 ring-1 ring-neutral-200 text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:bg-white"
                  : "bg-[#0a0b0f] ring-1 ring-[#27272a] focus:ring-2 focus:ring-purple-500/50 text-white placeholder:text-neutral-500"
              }`}
            />
          </div>

          {/* Code Snippet Preview (Rendered via Monaco Editor for exact language syntax coloring) */}
          {!isBlockSnippet && snippetData?.code && (
            <div className="space-y-1.5 text-left">
              <div className="flex items-center justify-between">
                <label className={`text-xs font-bold ${isLight ? "text-neutral-700" : "text-neutral-200"}`}>
                  Code Preview
                </label>
                <span className={`text-[10px] font-mono ${isLight ? "text-neutral-400" : "text-neutral-400"}`}>
                  {snippetData.code.trim().split("\n").length} line{snippetData.code.trim().split("\n").length > 1 ? "s" : ""}
                </span>
              </div>
              <div
                className={`rounded-xl overflow-hidden border-0 ${
                  isLight
                    ? "ring-1 ring-neutral-200"
                    : "ring-1 ring-[#27272a] bg-[#1e1e1e]"
                }`}
              >
                <Editor
                  height={`${Math.min(150, Math.max(68, (snippetData.code.trim().split("\n").length) * 19 + 20))}px`}
                  language={snippetData?.language === "javascript" ? "javascript" : "python"}
                  theme={isLight ? "vs" : "vs-dark"}
                  value={snippetData.code}
                  options={{
                    readOnly: true,
                    domReadOnly: true,
                    fontSize: 12,
                    lineNumbers: "off",
                    glyphMargin: false,
                    folding: false,
                    lineDecorationsWidth: 0,
                    lineNumbersMinChars: 0,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    automaticLayout: true,
                    renderLineHighlight: "none",
                    contextmenu: false,
                    padding: { top: 8, bottom: 8 },
                    scrollbar: {
                      vertical: "auto",
                      horizontal: "hidden",
                      verticalScrollbarSize: 5,
                    },
                    overviewRulerBorder: false,
                    overviewRulerLanes: 0,
                    hideCursorInOverviewRuler: true,
                  }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                isLight
                  ? "text-neutral-600 hover:bg-neutral-100"
                  : "text-neutral-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="px-5 py-2.5 rounded-xl text-xs font-extrabold bg-purple-600 hover:bg-purple-500 active:scale-95 text-white shadow-md shadow-purple-600/30 flex items-center gap-2 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-0"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Save to Backpack</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveSnippetDialog;
